import React, { useContext, useEffect, useState } from 'react';

import { isAxiosError } from 'axios';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { useIntercom } from 'react-use-intercom';

import CourseRequestDetails from './sections/CourseType/CourseRequestDetails/CourseRequestDetails';
import CourseRequestDiscounts from './sections/CourseType/CourseRequestDiscounts/CourseRequestDiscounts';
import PendingRequestPayments from './sections/CourseType/PendingRequestPayments/PendingRequestPayments';
import InsuranceRequestDetails from './sections/InsuranceType/InsuranceRequestDetails/InsuranceRequestDetails';
import InsuranceRequestDiscounts from './sections/InsuranceType/InsuranceRequestDiscounts/InsuranceRequestDiscounts';
import MaterialRequestDetails from './sections/MaterialType/MaterialRequestDetails/MaterialRequestDetails';
import MaterialRequestDiscounts from './sections/MaterialType/MaterialRequestDiscounts/MaterialRequestDiscounts';
import GraduationIcon from '../../../assets/icons/v3/graduation-icon.svg?url';
import HealthInsuranceIcon from '../../../assets/icons/v3/health-insurance.svg?url';
import BdsLabelPill from '../../../shared/components/v3/BdsLabelPill/BdsLabelPill';
import StatusBox from '../../../shared/components/v3/Cards/StatusBox/StatusBox';
import DetailsHero from '../../../shared/components/v3/Heros/DetailsHero/DetailsHero';
import BdsModal from '../../../shared/components/v3/Modals/BdsModal/BdsModal';
import Toast from '../../../shared/components/v3/Toast/Toast';
import { REQUEST_TABLE_ROUTE } from '../../../shared/constants/auth';
import {
  APPROVE_RF_REQUEST_MODAL,
  CANCEL_RF_HEALTH_REQUEST_MODAL,
  CANCEL_RF_TRAINING_REQUEST_MODAL,
  DEFAULT_BDS_MODAL,
  REJECT_RF_REQUEST_MODAL
} from '../../../shared/constants/modal';
import {
  FLEXIBLE_REMUNERATION_REQUEST_STATUS,
  FLEXIBLE_REMUNERATION_REQUESTS_TRAINING_TYPES,
  FLEXIBLE_REMUNERATION_REJECT_REASONS,
  FLEXIBLE_REMUNERATION_REQUESTS_SKU
} from '../../../shared/constants/requests';
import {
  CANCEL_INSURANCE,
  CANCEL_TRAINING,
  CLICK_ACTION,
  CONFIRM_INSURANCE,
  CONFIRM_TRAINING,
  RECANCEL_INSURANCE,
  RECANCEL_TRAINING,
  RECONFIRM_INSURANCE,
  RECONFIRM_TRAINING,
  REJECT_INSURANCE,
  REJECT_TRAINING,
  REREJECT_INSURANCE,
  REREJECT_TRAINING
} from '../../../shared/constants/tagManager';
import {
  APPROVE_RF_REQUEST_TOAST,
  CANCEL_RF_INSURANCE_REQUEST_TOAST,
  CANCEL_RF_REQUEST_TOAST,
  GENERIC_ERROR_TOAST,
  REJECT_RF_REQUEST_TOAST
} from '../../../shared/constants/toast';
import { CompanyContext } from '../../../shared/context/company/companyContext';
import { IBdsLabelPillImage } from '../../../shared/interfaces/bdsLabelPill';
import { IBdsModalContent } from '../../../shared/interfaces/bdsModal';
import { IFlexibleRemunerationRequest } from '../../../shared/interfaces/company';
import Company from '../../../shared/services/api/companies/companies';
import TagManagerService from '../../../shared/services/gtm/tagManagerService';
import { TRANSLATIONS_REQUESTS } from '../../../shared/translations/requests';
import type { HealthRequestBody, RequestStatus, TrainingRequestBody } from '../../../shared/types/flexibleRemuneration';
import { BdsLabelPillVariant } from '../../../shared/types/types';
import { formatDateToDDMMYYYY } from '../../../shared/utils/datetime/datetime';
import { RequestPayloadBuilder } from '../../../shared/utils/flexibleRemuneration/requestPayloadBuilder';
import { objectHasKey } from '../../../shared/utils/utils';
import './RequestDetails.scss';

type LocationState = { request?: IFlexibleRemunerationRequest };

const RequestDetails = () => {
  const [openModal, setOpenModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [type, setType] = useState<RequestStatus>();
  const [body, setBody] = useState<TrainingRequestBody | HealthRequestBody>();
  const [modalContent, setModalContent] = useState<IBdsModalContent>(DEFAULT_BDS_MODAL);
  const [openToast, setOpenToast] = useState(false);
  const [toastData, setToastData] = useState(GENERIC_ERROR_TOAST);
  const { companyData } = useContext(CompanyContext);
  const { show } = useIntercom();

  const navigate = useNavigate();
  const { t } = useTranslation();
  const location = useLocation();
  const { state } = location as { state: LocationState | null };
  const request = state?.request;
  const [localRequest, setLocalRequest] = useState<IFlexibleRemunerationRequest | undefined>(request);

  const goBack = () => navigate(REQUEST_TABLE_ROUTE);

  useEffect(() => {
    if (!request) {
      navigate(REQUEST_TABLE_ROUTE);
    }
  }, [request, navigate]);

  if (!localRequest) return null;
  const {
    details,
    member,
    amount,
    currency,
    status: localStatus,
    rejectReason,
    sku,
    id,
    type: requestType,
    createdAt,
    category
  } = localRequest;
  const isTrainingType = sku === FLEXIBLE_REMUNERATION_REQUESTS_SKU.training;
  const isHealthType = sku === FLEXIBLE_REMUNERATION_REQUESTS_SKU.health;
  const collaborator = `${member?.firstName ?? ''} ${member?.lastName ?? ''}`.trim();
  const baseDetailsProps = {
    collaborator,
    name: details?.name ?? '',
    type: details?.type,
    description: details?.description ?? ''
  };
  const isRejected = localStatus === FLEXIBLE_REMUNERATION_REQUEST_STATUS.rejected.value;
  const isRejectedByAdmin = rejectReason === 'REJECTED_BY_ADMIN';
  const heroIcon = isTrainingType ? GraduationIcon : HealthInsuranceIcon;
  const getGtmType = (detailsType: string | undefined): string => {
    switch (detailsType) {
      case 'course':
        return 'course';
      case 'material':
        return 'educational-material';
      default:
        return '';
    }
  };

  const onApproveTraining = (newPayments: TrainingRequestBody) => {
    setType('active');
    setBody(newPayments);
    setModalContent(APPROVE_RF_REQUEST_MODAL);
    setOpenModal(true);
    const gtmType = getGtmType(details?.type);
    TagManagerService.pushEvent(CLICK_ACTION, CONFIRM_TRAINING, { type: gtmType });
  };

  const onApproveHealth = (dates: HealthRequestBody) => {
    setType('active');
    setBody(dates);
    setModalContent(APPROVE_RF_REQUEST_MODAL);
    setOpenModal(true);
    TagManagerService.pushEvent(CLICK_ACTION, CONFIRM_INSURANCE, { type: category });
  };

  const onReject = () => {
    setType('rejected');
    setModalContent(REJECT_RF_REQUEST_MODAL);
    setOpenModal(true);

    const gtmType = isTrainingType ? getGtmType(details?.type) : category;
    const eventName = isTrainingType ? REJECT_TRAINING : REJECT_INSURANCE;
    TagManagerService.pushEvent(CLICK_ACTION, eventName, { type: gtmType });
  };

  const onCancel = () => {
    setType('cancelled');
    const modalData = isTrainingType ? CANCEL_RF_TRAINING_REQUEST_MODAL : CANCEL_RF_HEALTH_REQUEST_MODAL;
    setModalContent(modalData);
    setOpenModal(true);

    const gtmType = isTrainingType ? getGtmType(details?.type) : category;
    const eventName = isTrainingType ? CANCEL_TRAINING : CANCEL_INSURANCE;
    TagManagerService.pushEvent(CLICK_ACTION, eventName, { type: gtmType });
  };

  const updateRequestStatus = async () => {
    let hasError = false;
    let errorTag = undefined;
    let historyParam = {};
    try {
      setLoading(true);
      const payload = RequestPayloadBuilder(localRequest, {
        type: type as RequestStatus,
        newPayments: isTrainingType ? (body as TrainingRequestBody) : undefined,
        newHealthDates: isHealthType ? (body as HealthRequestBody) : undefined
      });
      await Company.updateCompanyFlexibleRemunerationRequest(companyData.id, id, payload);
    } catch (error: unknown) {
      hasError = true;
      if (isAxiosError(error) && error.response?.status === 422 && error.response?.data.description) {
        errorTag = error.response?.data.description;
      }
    } finally {
      setLoading(false);
      switch (type) {
        case 'active':
          if (errorTag) {
            historyParam = { errorTag: errorTag };
          } else {
            historyParam = { toast: hasError ? GENERIC_ERROR_TOAST : APPROVE_RF_REQUEST_TOAST };
          }
          TagManagerService.pushEvent(CLICK_ACTION, isTrainingType ? RECONFIRM_TRAINING : RECONFIRM_INSURANCE);
          navigate(REQUEST_TABLE_ROUTE, historyParam);
          break;
        case 'rejected':
          TagManagerService.pushEvent(CLICK_ACTION, isTrainingType ? REREJECT_TRAINING : REREJECT_INSURANCE);
          navigate(REQUEST_TABLE_ROUTE, { state: { toast: hasError ? GENERIC_ERROR_TOAST : REJECT_RF_REQUEST_TOAST } });
          break;
        case 'cancelled':
          TagManagerService.pushEvent(CLICK_ACTION, isTrainingType ? RECANCEL_TRAINING : RECANCEL_INSURANCE);
          if (hasError) {
            setToastData(GENERIC_ERROR_TOAST);
          } else {
            setToastData(isTrainingType ? CANCEL_RF_REQUEST_TOAST : CANCEL_RF_INSURANCE_REQUEST_TOAST);
            setLocalRequest((prev) =>
              prev ? { ...prev, status: FLEXIBLE_REMUNERATION_REQUEST_STATUS.cancelled.value } : prev
            );
          }
          setOpenToast(true);
      }
      handleCloseModal();
    }
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setType(undefined);
    setBody(undefined);
  };

  const handleCloseToast = () => {
    setOpenToast(false);
  };

  const openIntercom = () => {
    show();
  };

  const getRequestRejectMessage = () => {
    switch (rejectReason) {
      case FLEXIBLE_REMUNERATION_REJECT_REASONS.salaryPercentageExceeded:
        return t(TRANSLATIONS_REQUESTS.details.rejectReasons.salaryPercentage);
      case FLEXIBLE_REMUNERATION_REJECT_REASONS.accumulationLimitExceeded:
        return t(TRANSLATIONS_REQUESTS.details.rejectReasons.accumulationLimit);
      case FLEXIBLE_REMUNERATION_REJECT_REASONS.smiThresholdViolation:
        return t(TRANSLATIONS_REQUESTS.details.rejectReasons.SMIThreshold);
      default:
        return (
          <Trans
            i18nKey={TRANSLATIONS_REQUESTS.details.rejectReasons.internalFRError}
            components={{
              u: <button className="internal-error-button" onClick={openIntercom} />
            }}
          />
        );
    }
  };

  return (
    <>
      <div className="requests-detail-rf-container">
        <DetailsHero
          title={t(TRANSLATIONS_REQUESTS.details.hero.title, { request_type: requestType.toLowerCase() })}
          description={
            <>
              {localStatus && objectHasKey(localStatus, FLEXIBLE_REMUNERATION_REQUEST_STATUS) ? (
                <BdsLabelPill
                  text={String(t(FLEXIBLE_REMUNERATION_REQUEST_STATUS[localStatus].text)).toUpperCase()}
                  variant={FLEXIBLE_REMUNERATION_REQUEST_STATUS[localStatus].tagVariant as BdsLabelPillVariant}
                  iconLeft={FLEXIBLE_REMUNERATION_REQUEST_STATUS[localStatus].icon as IBdsLabelPillImage}
                />
              ) : (
                <div />
              )}
              <div className="description flex flex-row justify-start items-center">
                {t(TRANSLATIONS_REQUESTS.details.hero.description, {
                  date: formatDateToDDMMYYYY(createdAt),
                  interpolation: { escapeValue: false }
                })}
              </div>
            </>
          }
          color="hero-requests"
          backAction={goBack}
          icon={heroIcon}
        />

        <div className="detail-container flex flex-col gap-10 px-9 py-8">
          {isRejected && rejectReason && !isRejectedByAdmin && (
            <StatusBox type="error" message={getRequestRejectMessage()} />
          )}
          {isTrainingType && details?.type === FLEXIBLE_REMUNERATION_REQUESTS_TRAINING_TYPES.material && (
            <>
              <MaterialRequestDetails request={baseDetailsProps} />
              <MaterialRequestDiscounts request={localRequest} onApprove={onApproveTraining} onReject={onReject} />
            </>
          )}
          {isTrainingType && details?.type === FLEXIBLE_REMUNERATION_REQUESTS_TRAINING_TYPES.course && (
            <>
              <CourseRequestDetails
                request={{
                  ...baseDetailsProps,
                  startDate: details?.startDate,
                  endDate: details?.endDate,
                  centerName: details?.centerName,
                  contactPerson: details?.contactData?.contactName,
                  phone: details?.contactData?.phone,
                  contactEmail: details?.contactData?.email,
                  status: localStatus
                }}
              />
              {localStatus === FLEXIBLE_REMUNERATION_REQUEST_STATUS.pending.value ? (
                <PendingRequestPayments
                  amount={amount}
                  currency={currency}
                  onApprove={onApproveTraining}
                  onReject={onReject}
                />
              ) : (
                <CourseRequestDiscounts request={localRequest} onCancel={onCancel} />
              )}
            </>
          )}
          {isHealthType && (
            <>
              <InsuranceRequestDetails request={localRequest} />
              <InsuranceRequestDiscounts
                request={localRequest}
                onApprove={onApproveHealth}
                onReject={onReject}
                onCancel={onCancel}
              />
            </>
          )}
        </div>
      </div>
      <BdsModal
        reverseButtons={true}
        open={openModal}
        title={t(modalContent.title as string, { request_type: requestType.toLowerCase() })}
        description={t(modalContent.description as string, {
          request_type: requestType.toLowerCase(),
          employee_name: collaborator
        })}
        icon={<modalContent.icon />}
        primaryAction={{
          label: t(modalContent.primaryActionText as string),
          onClick: () => {
            updateRequestStatus();
          },
          disabled: loading,
          loadingType: modalContent.primaryLoadingType,
          loadingText: modalContent.primaryLoadingText ? t(modalContent.primaryLoadingText) : undefined
        }}
        secondaryAction={{
          label: t(modalContent.secondaryActionText as string),
          onClick: handleCloseModal,
          disabled: loading
        }}
        loading={loading}
      />
      <Toast
        open={openToast}
        handleClose={handleCloseToast}
        buttonAction={handleCloseToast}
        title={t(toastData.title)}
        description={t(toastData.description)}
        icon={toastData.icon}
        button={toastData.button}
      />
    </>
  );
};

export default RequestDetails;
